import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BookOpenIcon,
  AcademicCapIcon,
  CalendarIcon,
  BriefcaseIcon,
  IdentificationIcon,
  MapPinIcon,
  GlobeAltIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { showSuccess, showError, showWarning } from '../utils/alert';
import apiClient from '../api/client';
import { socket } from '../services/socket'; // assuming socket is exported from services/socket

export default function AddTeacher() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    date_of_birth: '',
    gender: '',
    nationality: 'Sierra Leonean',
    address: '',
    city: '',
    state: '',
    country: 'Sierra Leone',
    phone: '',
    email: '',
    qualification: '',
    teacher_status: '',
    pin_code: '',
    nin_number: '',
    class_taught: '',
    years_of_experience: 0,
    joining_date: '',
    status: 'active',
  });

  // ─── Fetch classes (with background option) ───
  const fetchClasses = async (options = { background: false }) => {
    try {
      const response = await apiClient.get('/classes', {
        params: { limit: 100, status: 'active' }
      });
      let data = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data.data) {
          data = response.data.data;
        }
      }
      const availableClasses = data.filter(cls => !cls.teachers || cls.teachers.length === 0);
      setClasses(availableClasses);
    } catch (error) {
      if (!options.background) {
        await showError({ text: 'Failed to fetch classes' });
      }
      console.error('Error fetching classes:', error);
    }
  };

  // ─── Initial load ───
  useEffect(() => {
    fetchClasses();

    // ─── Socket listener for class updates (background refresh) ───
    const handleClassUpdate = (event) => {
      console.log('📡 Class update received:', event);
      // Refresh classes in background – no loader, no notification
      fetchClasses({ background: true });
    };

    socket.on('class:updated', handleClassUpdate);

    return () => {
      socket.off('class:updated', handleClassUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      showWarning({ text: 'First name is required' });
      return false;
    }
    if (!formData.last_name.trim()) {
      showWarning({ text: 'Last name is required' });
      return false;
    }
    if (!formData.email.trim()) {
      showWarning({ text: 'Email is required' });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      showWarning({ text: 'Please enter a valid email address' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        class_id: selectedClassId || undefined,
        years_of_experience: parseInt(formData.years_of_experience) || 0,
      };

      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === null) {
          delete submitData[key];
        }
      });

      await apiClient.post('/teachers', submitData);

      await showSuccess({ text: 'Teacher added successfully! A user account has been created automatically.' });
      navigate('/app/teachers');
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to add teacher';
      await showError({ text: msg });
      console.error('Error adding teacher:', error);
    } finally {
      setLoading(false);
    }
  };

  const getClassLabel = (cls) => {
    return `${cls.name} (${cls.level} - ${cls.faculty})`;
  };

  return (
    <div className="space-y-4 sm:space-y-6 max-w-4xl mx-auto px-4 sm:px-0">
      {/* Back button */}
      <button
        onClick={() => navigate('/app/teachers')}
        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors text-sm sm:text-base"
      >
        <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        Back to Teachers
      </button>

      {/* Main card – flat, no shadow, subtle border */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-primary-700 mb-4 sm:mb-6">Add New Teacher</h1>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Personal Information */}
          <div className="border-b border-gray-100 pb-4">
            <h2 className="text-base sm:text-lg font-semibold text-primary-700 mb-3 sm:mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  First Name *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="First name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Last Name *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Middle Name
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="Middle name (optional)"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Date of Birth
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="+232 76 123456"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Email *
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="teacher@school.edu"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="border-b border-gray-100 pb-4">
            <h2 className="text-base sm:text-lg font-semibold text-primary-700 mb-3 sm:mb-4">Professional Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Qualification
                </label>
                <div className="relative">
                  <AcademicCapIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="e.g., TC, HTC, HTC/P"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Teacher Status
                </label>
                <select
                  name="teacher_status"
                  value={formData.teacher_status}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                >
                  <option value="">Select Status</option>
                  <option value="School Leader">School Leader</option>
                  <option value="Deputy">Deputy</option>
                  <option value="Senior Teacher">Senior Teacher</option>
                  <option value="Teacher">Teacher</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  PIN Code
                </label>
                <div className="relative">
                  <IdentificationIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    name="pin_code"
                    value={formData.pin_code}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="PIN code"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  NIN Number
                </label>
                <div className="relative">
                  <IdentificationIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    name="nin_number"
                    value={formData.nin_number}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="National ID number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Class Taught
                </label>
                <div className="relative">
                  <BookOpenIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    name="class_taught"
                    value={formData.class_taught}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="e.g., I, II–III, ALL, VI"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Years of Experience
                </label>
                <div className="relative">
                  <BriefcaseIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="number"
                    name="years_of_experience"
                    value={formData.years_of_experience}
                    onChange={handleChange}
                    min="0"
                    max="60"
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Joining Date
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="date"
                    name="joining_date"
                    value={formData.joining_date}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="on_leave">On Leave</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Assign Class (optional)
                  <span className="text-gray-400 text-xs ml-2">(Only classes without a teacher are shown)</span>
                </label>
                <select
                  name="class_id"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                >
                  <option value="">Select a class (optional)</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {getClassLabel(cls)}
                    </option>
                  ))}
                </select>
                {classes.length === 0 && (
                  <p className="text-sm text-yellow-600 mt-2">
                    No available classes without a teacher at the moment.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="border-b border-gray-100 pb-4">
            <h2 className="text-base sm:text-lg font-semibold text-primary-700 mb-3 sm:mb-4">Address Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Address
                </label>
                <div className="relative">
                  <MapPinIcon className="absolute left-3 top-3 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="2"
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="Street address"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                  placeholder="City"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  State/Province
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                  placeholder="State"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                  Country
                </label>
                <div className="relative">
                  <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-primary-700 text-sm sm:text-base transition-colors"
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Buttons – flat, no gradient */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/app/teachers')}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-primary-700 text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Adding...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  Add Teacher
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}